<script setup>
/**
 * A tarot card kept in the Journal: its picture when there is one, else the
 * drawn plum card with its numeral, a mark and its name (the prototype's card).
 */
import { computed, ref } from 'vue'
import JournalGlyph from './JournalGlyph.vue'

const props = defineProps({
  card: { type: Object, default: null },
  w: { type: Number, default: 58 },
})

const MAJOR = { 'The Fool': '0', 'The Magician': 'I', 'The High Priestess': 'II', 'The Empress': 'III', 'The Emperor': 'IV', 'The Hierophant': 'V', 'The Lovers': 'VI', 'The Chariot': 'VII', Strength: 'VIII', 'The Hermit': 'IX', 'Wheel of Fortune': 'X', Justice: 'XI', 'The Hanged Man': 'XII', Death: 'XIII', Temperance: 'XIV', 'The Devil': 'XV', 'The Tower': 'XVI', 'The Star': 'XVII', 'The Moon': 'XVIII', 'The Sun': 'XIX', Judgement: 'XX', 'The World': 'XXI' }
const RANK = { Ace: 'A', Two: 'II', Three: 'III', Four: 'IV', Five: 'V', Six: 'VI', Seven: 'VII', Eight: 'VIII', Nine: 'IX', Ten: 'X', Page: 'P', Knight: 'Kn', Queen: 'Q', King: 'K' }

const name = computed(() => (props.card && props.card.name) || '')
const numeral = computed(() => {
  const n = name.value.replace(/\s*\(reversed\)/i, '')
  if (MAJOR[n] !== undefined) return MAJOR[n]
  return RANK[n.split(' ')[0]] || ''
})
const mark = computed(() => {
  const n = name.value.toLowerCase()
  if (n.includes('strength')) return 'strength'
  if (n.includes('star')) return 'star'
  if (n.includes('sword')) return 'swords'
  if (n.includes('cup')) return 'cups'
  if (n.includes('wand')) return 'wands'
  if (n.includes('pentacle') || n.includes('coin')) return 'pentacles'
  if (n.includes('chariot') || n.includes('wheel')) return 'wheel'
  if (n.includes('moon')) return 'moon'
  return 'star'
})
const broken = ref(false)
const image = computed(() => (!broken.value && props.card && props.card.image) || '')
</script>

<template>
  <span class="tc" :class="{ pic: !!image }" :style="{ '--w': w + 'px' }" role="img" :aria-label="name">
    <img v-if="image" :src="image" alt="" @error="broken = true" />
    <template v-else>
      <span class="n">{{ numeral }}</span>
      <JournalGlyph :name="mark" class="m" />
      <span class="l">{{ name.replace(/^The /, 'The ') }}</span>
    </template>
  </span>
</template>

<style scoped>
.tc { width: var(--w); height: calc(var(--w) * 1.62); border-radius: calc(var(--w) * .1); flex: none; position: relative; display: flex; flex-direction: column; align-items: center; justify-content: space-between;
  padding: calc(var(--w) * .08) 2px; box-sizing: border-box; background: linear-gradient(160deg, #432470, #1b0d33); border: 1.5px solid #c9a94e; color: #ffdfa3; box-shadow: 0 3px 8px rgba(42, 24, 69, .35); overflow: hidden; }
.tc.pic { padding: 0; background: #1b0d33; }
.tc img { width: 100%; height: 100%; object-fit: cover; display: block; }
.n { font: 600 calc(var(--w) * .15) 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: .05em; opacity: .85; }
.m { width: calc(var(--w) * .5); height: calc(var(--w) * .5); }
.l { font: 600 calc(var(--w) * .11)/1.1 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: .04em; text-align: center; opacity: .95; text-transform: uppercase; }
</style>
